import { registerRoute } from '../router';
import { apiFetch, ApiError } from '../api';
import { escapeHtml } from '../format';
import { openModal } from '../components/modal';

interface ApiKey {
  id: string;
  label: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

let apiKeys: ApiKey[] = [];

async function loadApiKeys(container: HTMLElement): Promise<void> {
  apiKeys = await apiFetch<ApiKey[]>('/api/api-keys');
  renderList(container);
}

function showKeyOnce(key: string): void {
  const { root, close } = openModal(
    'Save this key now',
    `
    <p style="font-size:0.85rem;color:var(--clr-text-muted);margin-bottom:14px;">
      This is the only time the full key is shown. Use it as
      <code>Authorization: Bearer ${escapeHtml(key.slice(0, 6))}…</code> — it acts with your own
      account's permissions on every request.
    </p>
    <div class="field">
      <label>API key</label>
      <input class="input" readonly value="${escapeHtml(key)}" id="apikey-value">
    </div>
    <div style="display:flex;justify-content:flex-end;">
      <button type="button" class="btn btn--primary" id="apikey-close">Done</button>
    </div>
  `,
  );
  root.querySelector('#apikey-close')?.addEventListener('click', close);
}

function renderList(container: HTMLElement): void {
  const listEl = container.querySelector('#apikeys-list')!;

  if (apiKeys.length === 0) {
    listEl.innerHTML = `<p style="color:var(--clr-text-muted);">No API keys yet.</p>`;
    return;
  }

  listEl.innerHTML = apiKeys
    .map(
      (key) => `
    <div class="card" style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
      <div>
        <div style="font-weight:600;">${escapeHtml(key.label)}</div>
        <div style="color:var(--clr-text-muted);font-size:0.85rem;margin-top:4px;">
          Created ${escapeHtml(key.createdAt)} — last used ${key.lastUsedAt ? escapeHtml(key.lastUsedAt) : 'never'}
        </div>
      </div>
      <div style="display:flex;align-items:center;gap:10px;">
        <span class="badge ${key.revokedAt ? 'badge--danger' : 'badge--success'}">${key.revokedAt ? 'revoked' : 'active'}</span>
        ${!key.revokedAt ? `<button class="btn btn--ghost" data-action="revoke" data-id="${key.id}">Revoke</button>` : ''}
      </div>
    </div>`,
    )
    .join('');

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="revoke"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Revoke this key? Anything using it will stop working immediately.')) return;
      await apiFetch(`/api/api-keys/${btn.dataset.id}`, { method: 'DELETE' });
      await loadApiKeys(container);
    });
  });
}

function openCreateModal(container: HTMLElement): void {
  const { root, close } = openModal(
    'New API key',
    `
    <form id="apikey-form">
      <div class="field">
        <label for="apikey-label">Label</label>
        <input class="input" id="apikey-label" required placeholder="e.g. order system integration">
      </div>
      <p class="error-text" id="apikey-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="apikey-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">Create</button>
      </div>
    </form>
  `,
  );

  root.querySelector('#apikey-cancel')?.addEventListener('click', close);

  root.querySelector('#apikey-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#apikey-error')!;
    errorEl.textContent = '';

    const label = (root.querySelector('#apikey-label') as HTMLInputElement).value.trim();

    try {
      const created = await apiFetch<ApiKey & { key: string }>('/api/api-keys', { method: 'POST', body: JSON.stringify({ label }) });
      close();
      showKeyOnce(created.key);
      await loadApiKeys(container);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

registerRoute({
  path: '/api-keys',
  title: 'API Keys',
  render: ({ container }) => {
    container.innerHTML = `
      <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
        <div>
          <h1>API Keys</h1>
          <p>Generate keys for external systems to call ZhuMobile Connect's API on your behalf — each key acts with your own account's permissions.</p>
        </div>
        <button class="btn btn--primary" id="new-apikey-btn">+ New API key</button>
      </div>
      <div id="apikeys-list">Loading…</div>
    `;

    container.querySelector('#new-apikey-btn')?.addEventListener('click', () => openCreateModal(container));

    loadApiKeys(container).catch(() => {
      container.querySelector('#apikeys-list')!.textContent = 'Could not load API keys.';
    });
  },
});
